// frontend/src/components/WaiverGate.js
//
// ESIGN-WAIVER-CONSENT-001 — pre-handoff gate page (Zuri spec §16.2).
//
// Trigger: customer is in the first-booking flow at the §6 gate point and
// `User.consentSigned` OR `User.releaseSigned` is false. The gate captures
// DOB (drives §8 minor handling), then fires `POST /api/waivers/initiate`
// and full-navigates to SignWell's signing_url.
//
// Spec references:
//   docs/design/ESIGN-WAIVER-CONSENT-001-design.md
//     §16.2 — pre-handoff layout + locked copy
//     §16.3 — redirect-out behavior (full nav, NOT new tab)
//     §16.6 — error state on initiate failure
//     §8    — DOB-driven minor handling buckets (under-15 / 15-17 / >=18)
//
// Visual DoD (per §16.7):
//   - Page title verbatim: "Before you book your first session"
//   - Forms list as bulleted (consent + release)
//   - DOB picker required; CTA disabled until valid
//   - 15-17 surfaces guardian printed name input
//   - Under-15 surfaces guardian input PLUS in-suite advisory card
//   - Primary CTA verbatim: "Continue to sign forms"
//   - On CTA tap -> POST /api/waivers/initiate -> window.location.href = signing_url
//   - pendingWaiverEnvelopeId persisted to localStorage before redirect

import React, { useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  initiateWaiverSigning,
  PENDING_WAIVER_ENVELOPE_KEY,
} from "../services/waiverApi";
import "./WaiverGate.css";

// Maximum in-session retry count for initiate failures before the support
// link is surfaced more prominently per §16.6.
const MAX_RETRY_BEFORE_PROMINENT_SUPPORT = 3;

/**
 * Compute age in whole years from a yyyy-mm-dd string. Returns null if the
 * input cannot be parsed. Uses calendar-year math (not 365.25 days) so the
 * threshold flips on the customer's actual birthday, not a few days early.
 */
function computeAgeFromDob(dob) {
  if (!dob) return null;
  const parsed = new Date(`${dob}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - parsed.getFullYear();
  const monthDelta = today.getMonth() - parsed.getMonth();
  if (
    monthDelta < 0 ||
    (monthDelta === 0 && today.getDate() < parsed.getDate())
  ) {
    age -= 1;
  }
  if (age < 0 || age > 130) return null;
  return age;
}

const WaiverGate = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get("returnTo") || "/book-session";

  const [dob, setDob] = useState("");
  const [guardianName, setGuardianName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [retryCount, setRetryCount] = useState(0);

  const age = useMemo(() => computeAgeFromDob(dob), [dob]);

  // Minor-handling buckets per §8.2.
  const isMinor = age !== null && age < 18;
  const isUnder15 = age !== null && age < 15;

  // CTA enabled only when DOB is valid AND guardian name is present (when
  // required by minor-handling rules).
  const ctaEnabled = useMemo(() => {
    if (submitting) return false;
    if (age === null) return false;
    if (isMinor && !guardianName.trim()) return false;
    return true;
  }, [submitting, age, isMinor, guardianName]);

  const handleContinue = async (e) => {
    e.preventDefault();
    if (!ctaEnabled) return;

    setSubmitting(true);
    setErrorMessage("");

    try {
      const response = await initiateWaiverSigning();
      const { signingUrl, envelopeId } = response.data || {};

      if (!signingUrl || !envelopeId) {
        // Defensive — backend wrapper guarantees both, but PR B's controller
        // already maps a vendor-incomplete response to 502; if we somehow
        // get a 200 without both fields, treat as error.
        throw new Error("waiver_response_missing_fields");
      }

      // Persist the envelopeId so the return page can correlate the redirect
      // back from SignWell with the in-flight envelope (Zuri §16.3 step 2).
      try {
        localStorage.setItem(PENDING_WAIVER_ENVELOPE_KEY, envelopeId);
        // Capture returnTo so the success state can resume the booking flow.
        localStorage.setItem("pendingWaiverReturnTo", returnTo);
        // DOB capture is local-only at the gate (no BE persistence in v1 — DOB
        // travels via SignWell metadata in a future iteration; stash for the
        // return page in case it's surfaced there).
        if (dob) localStorage.setItem("pendingWaiverDob", dob);
        if (guardianName) {
          localStorage.setItem("pendingWaiverGuardianName", guardianName.trim());
        }
      } catch (storageErr) {
        // localStorage write can fail in private mode / quota; non-fatal —
        // the return page will degrade gracefully (poll without correlation).
      }

      // Full navigation to SignWell hosted-signing page (Zuri §16.3 step 3).
      window.location.href = signingUrl;
    } catch (err) {
      const status = err?.response?.status;
      const apiCode = err?.response?.data?.error;
      let copy;
      if (apiCode === "forbidden_cross_user_initiate") {
        copy =
          "We can't start the signing flow for this account. Please contact support.";
      } else if (status === 503 || apiCode === "signwell_network_error") {
        copy =
          "Couldn't reach the signing service. Please check your connection and try again.";
      } else if (status >= 500 || status === 502) {
        copy =
          "Couldn't start the signing flow. Please try again. If this keeps happening, email support@divitiadorastudio.com.";
      } else {
        copy =
          "Couldn't start the signing flow. Please try again. If this keeps happening, email support@divitiadorastudio.com.";
      }
      setErrorMessage(copy);
      setRetryCount((c) => c + 1);
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(returnTo);
  };

  const showProminentSupport = retryCount >= MAX_RETRY_BEFORE_PROMINENT_SUPPORT;

  return (
    <div className="waiver-gate-page">
      <form className="waiver-gate-card" onSubmit={handleContinue} noValidate>
        <h1 className="waiver-gate-title">Before you book your first session</h1>

        <p className="waiver-gate-lede">
          We need a quick electronic signature on two standard intake forms.
          This takes about 2 minutes.
        </p>

        <ul className="waiver-gate-forms-list">
          <li>Consent to Receive Infrared &amp; Red Light Therapy</li>
          <li>Client Acknowledgment &amp; Release of Liability</li>
        </ul>

        <p className="waiver-gate-handoff-helper">
          You'll sign on a secure signing page. We'll send you email copies of
          both signed forms when you're done.
        </p>

        {errorMessage && (
          <div
            className="waiver-gate-error"
            role="alert"
            aria-live="assertive"
          >
            <span>{errorMessage}</span>
            {showProminentSupport && (
              <p className="waiver-gate-support-link">
                Trouble signing? Email{" "}
                <a href="mailto:support@divitiadorastudio.com">
                  support@divitiadorastudio.com
                </a>
                .
              </p>
            )}
          </div>
        )}

        <div className="waiver-gate-form-group">
          <label htmlFor="waiver-gate-dob">
            Date of birth <span aria-hidden="true">*</span>
          </label>
          <input
            id="waiver-gate-dob"
            type="date"
            value={dob}
            onChange={(e) => setDob(e.target.value)}
            required
            aria-required="true"
            disabled={submitting}
          />
          {dob && age === null && (
            <p className="waiver-gate-helper-error">
              Please enter a valid date of birth.
            </p>
          )}
          {!dob && (
            <p className="waiver-gate-helper-text">
              Please enter your date of birth.
            </p>
          )}
        </div>

        {isMinor && (
          <div className="waiver-gate-form-group waiver-gate-guardian-group">
            <label htmlFor="waiver-gate-guardian">
              Guardian printed name <span aria-hidden="true">*</span>
            </label>
            <input
              id="waiver-gate-guardian"
              type="text"
              value={guardianName}
              onChange={(e) => setGuardianName(e.target.value)}
              required
              aria-required="true"
              disabled={submitting}
              placeholder="Parent or legal guardian's full name"
            />
          </div>
        )}

        {isUnder15 && (
          <div className="waiver-gate-advisory-card" role="note">
            <strong>In-suite presence required</strong>
            <p>
              For clients under 15, a parent or legal guardian must remain
              physically present in the suite during the entire session. The
              guardian named above must be the same person attending in person.
            </p>
          </div>
        )}

        <div className="waiver-gate-cta-row">
          <button
            type="submit"
            className="waiver-gate-cta-primary"
            disabled={!ctaEnabled}
            aria-disabled={!ctaEnabled}
          >
            {submitting ? (
              <span>
                <span className="waiver-gate-spinner" aria-hidden="true" />
                Preparing your signing page...
              </span>
            ) : (
              "Continue to sign forms"
            )}
          </button>
          <button
            type="button"
            className="waiver-gate-cta-secondary"
            onClick={handleCancel}
            disabled={submitting}
          >
            Cancel and return to booking
          </button>
        </div>

        <p className="waiver-gate-fineprint">
          Need help?{" "}
          <Link to="/contact">Contact the studio</Link> or email{" "}
          <a href="mailto:support@divitiadorastudio.com">
            support@divitiadorastudio.com
          </a>
          .
        </p>
      </form>
    </div>
  );
};

export default WaiverGate;
